"use client"

import { motion } from "framer-motion"
import {
  BookOpenIcon,
  BotIcon,
  ChevronRightIcon,
  CloudIcon,
  CodeIcon,
  DownloadIcon,
  HomeIcon,
  ListIcon,
  MenuIcon,
  RefreshCwIcon,
  SparklesIcon,
  TerminalSquareIcon,
  UploadIcon,
  XIcon,
  ZapIcon
} from "lucide-react"
import Link from "next/link"
import { useCallback, useEffect, useState } from "react"
import {
  SUBSCRIPTION_PLANS,
  SubscriptionPlanType
} from "@/api/domainModels/subscriptionPlan"
import { CopyCodeBlock } from "@/components/ui/CopyCodeBlock"
import { Logo } from "../../components/ui/Logo"

const guides = [
  { label: "Overview", id: "overview" },
  { label: "Installation", id: "installation" },
  { label: "Quick start", id: "quick-start" },
  { label: "Plans & limits", id: "plans" },
  { label: "Authentication", id: "authentication" },
  { label: "Configuration", id: "configuration" },
  { label: "On-disk layout", id: "on-disk-layout" },
  { label: "init", id: "cmd-init" },
  { label: "login", id: "cmd-login" },
  { label: "status", id: "cmd-status" },
  { label: "env", id: "cmd-env" },
  { label: "export", id: "cmd-export" },
  { label: "seed", id: "cmd-seed" },
  { label: "list", id: "cmd-list" },
  { label: "history", id: "cmd-history" },
  { label: "check", id: "cmd-check" },
  { label: "generate", id: "cmd-generate" },
  { label: "refresh", id: "cmd-refresh" },
  { label: "pull", id: "cmd-pull" },
  { label: "push", id: "cmd-push" },
  { label: "MCP Server", id: "mcp" },
  { label: "Environment variables", id: "env-vars" },
  { label: "Playwright", id: "e2e-playwright" },
  { label: "CI / CD", id: "cicd" }
]

type FlagRow = {
  flag: string
  description: string
  required: boolean
  env?: string
  default?: string
}

function FlagTable({ flags }: { flags: FlagRow[] }) {
  return (
    <div className="mt-5 overflow-x-auto rounded-xl border border-gray-800">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-gray-800 bg-gray-900/60">
            <th className="px-4 py-3 text-left font-medium text-gray-400">
              Flag
            </th>
            <th className="px-4 py-3 text-left font-medium text-gray-400">
              Description
            </th>
            <th className="px-4 py-3 text-left font-medium text-gray-400">
              Required
            </th>
            <th className="px-4 py-3 text-left font-medium text-gray-400">
              Env / Default
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-800/60">
          {flags.map((f) => (
            <tr
              key={f.flag}
              className="bg-gray-900/30 transition-colors hover:bg-gray-900/60"
            >
              <td className="whitespace-nowrap px-4 py-3 font-mono text-emerald-300">
                {f.flag}
              </td>
              <td className="px-4 py-3 text-gray-300">{f.description}</td>
              <td className="px-4 py-3">
                {f.required ? (
                  <span className="rounded-full bg-emerald-500/10 px-2 py-0.5 text-xs font-medium text-emerald-300">
                    Yes
                  </span>
                ) : (
                  <span className="rounded-full bg-gray-800 px-2 py-0.5 text-xs text-gray-400">
                    No
                  </span>
                )}
              </td>
              <td className="px-4 py-3 font-mono text-xs text-gray-400">
                {f.env && (
                  <span className="text-yellow-400/80">{f.env}</span>
                )}
                {f.env && f.default && (
                  <span className="text-gray-500"> / </span>
                )}
                {f.default && <span>{f.default}</span>}
                {!f.env && !f.default && (
                  <span className="text-gray-500">&mdash;</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function CodeBlock({ children }: { children: string }) {
  return (
    <CopyCodeBlock
      text={children}
      className="mt-5 rounded-xl border border-gray-800 bg-gray-950/80 p-4"
    >
      <pre className="overflow-x-auto pr-12 text-sm text-emerald-200">
        <code>{children}</code>
      </pre>
    </CopyCodeBlock>
  )
}

function CommandSection({
  id,
  icon: Icon,
  title,
  description,
  flags,
  example,
  notes,
  pro = false
}: {
  id: string
  icon: React.ComponentType<{ className?: string }>
  title: string
  description: string
  flags: FlagRow[]
  example: string
  notes?: string[]
  pro?: boolean
}) {
  return (
    <section id={id} className="mt-14 scroll-mt-20">
      <div className="flex items-center gap-3">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-emerald-500/10 ring-1 ring-emerald-500/25">
          <Icon className="h-4 w-4 text-emerald-300" />
        </div>
        <h2 className="text-2xl font-semibold tracking-tight text-gray-50">
          <span className="font-mono text-emerald-300">seedmancer {title}</span>
        </h2>
        {pro && (
          <span className="inline-flex items-center rounded-full bg-emerald-500/10 px-2.5 py-0.5 text-xs font-semibold text-emerald-400 ring-1 ring-emerald-500/25">
            Pro
          </span>
        )}
      </div>
      <p className="mt-3 text-base leading-7 text-gray-400">{description}</p>
      <FlagTable flags={flags} />
      <div className="mt-5">
        <p className="mb-2 text-xs font-semibold uppercase tracking-widest text-gray-500">
          Example
        </p>
        <CodeBlock>{example}</CodeBlock>
      </div>
      {notes && notes.length > 0 && (
        <ul className="mt-4 space-y-2">
          {notes.map((note) => (
            <li
              key={note}
              className="flex items-start gap-2.5 text-sm text-gray-400"
            >
              <BookOpenIcon className="mt-0.5 h-4 w-4 shrink-0 text-emerald-500/70" />
              <span>{note}</span>
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}

function SidebarContent({
  activeId,
  onNavigate
}: {
  activeId?: string
  onNavigate?: () => void
}) {
  return (
    <>
      <div className="border-b border-gray-800/80 px-6 py-4">
        <Link
          href="/"
          className="inline-flex min-w-0 max-w-full items-center"
        >
          <Logo variant="wordmark" className="min-w-0" />
        </Link>
      </div>

      <div className="px-5 py-5">
        <div className="space-y-1">
          <Link
            href="/"
            onClick={onNavigate}
            className="flex items-center gap-2 rounded-lg px-3 py-2 text-sm text-gray-300 transition hover:bg-gray-900 hover:text-gray-100"
          >
            <HomeIcon className="h-4 w-4 text-emerald-300" />
            Homepage
          </Link>
          <a
            href="https://github.com/KazanKK/Seedmancer"
            target="_blank"
            rel="noreferrer"
            className="flex items-center gap-2 rounded-lg px-3 py-2 text-sm text-gray-300 transition hover:bg-gray-900 hover:text-gray-100"
          >
            <CodeIcon className="h-4 w-4 text-emerald-300" />
            GitHub
          </a>
        </div>

        <div className="mt-7">
          <p className="px-3 text-xs font-semibold uppercase tracking-[0.2em] text-gray-500">
            CLI Reference
          </p>
          <div className="relative mt-3 space-y-0.5">
            {guides.map((guide) => {
              const isActive = activeId === guide.id
              return (
                <a
                  key={guide.id}
                  href={`#${guide.id}`}
                  onClick={onNavigate}
                  className={`relative block rounded-lg py-2 pl-4 pr-3 text-sm transition ${
                    isActive
                      ? "bg-emerald-500/10 font-medium text-emerald-300"
                      : "text-gray-400 hover:bg-gray-900 hover:text-gray-100"
                  }`}
                >
                  {isActive && (
                    <span className="absolute inset-y-1.5 left-0 w-0.5 rounded-full bg-emerald-400" />
                  )}
                  {guide.label}
                </a>
              )
            })}
          </div>
        </div>
      </div>
    </>
  )
}

export default function DocsPage() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [activeId, setActiveId] = useState<string>(guides[0].id)

  const closeMobileMenu = useCallback(() => setMobileMenuOpen(false), [])

  // Scroll-spy: observe every section heading and update activeId to whichever
  // one is closest to the top of the viewport.
  useEffect(() => {
    const sectionIds = guides.map((g) => g.id)
    const headingEls = sectionIds
      .map((id) => document.getElementById(id))
      .filter((el): el is HTMLElement => el !== null)

    const observer = new IntersectionObserver(
      (entries) => {
        // Pick the topmost visible section (smallest bounding rect top >= 0).
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)
        if (visible.length > 0) {
          setActiveId(visible[0].target.id)
        }
      },
      {
        rootMargin: "-20% 0px -70% 0px",
        threshold: 0,
      }
    )

    headingEls.forEach((el) => observer.observe(el))
    return () => observer.disconnect()
  }, [])

  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = "hidden"
      return () => {
        document.body.style.overflow = ""
      }
    }
  }, [mobileMenuOpen])

  return (
    <main className="min-h-screen bg-gray-950 text-gray-100">
      <div className="flex min-h-screen">
        {/* Desktop sidebar */}
        <aside className="hidden w-72 shrink-0 border-r border-gray-800/80 bg-gray-950 lg:block">
          <div className="sticky top-0 h-screen overflow-y-auto">
            <SidebarContent activeId={activeId} />
          </div>
        </aside>

        {/* Mobile sidebar overlay */}
        {mobileMenuOpen && (
          <div className="fixed inset-0 z-50 lg:hidden">
            <div
              className="absolute inset-0 bg-black/60 backdrop-blur-sm"
              onClick={closeMobileMenu}
              onKeyDown={(e) => e.key === "Escape" && closeMobileMenu()}
              role="button"
              tabIndex={0}
              aria-label="Close menu"
            />
            <div className="relative h-full w-72 overflow-y-auto border-r border-gray-800/80 bg-gray-950">
              <button
                onClick={closeMobileMenu}
                className="absolute right-3 top-4 rounded-lg p-1.5 text-gray-400 transition-colors hover:bg-gray-900 hover:text-gray-200"
                aria-label="Close menu"
              >
                <XIcon className="h-5 w-5" />
              </button>
              <SidebarContent activeId={activeId} onNavigate={closeMobileMenu} />
            </div>
          </div>
        )}

        {/* Main content */}
        <div className="min-w-0 flex-1">
          <header className="sticky top-0 z-20 border-b border-gray-800/80 bg-gray-950/95 backdrop-blur lg:hidden">
            <div className="flex items-center px-4 py-3">
              <button
                onClick={() => setMobileMenuOpen(true)}
                className="rounded-lg p-1.5 text-gray-400 transition-colors hover:bg-gray-900 hover:text-gray-200"
                aria-label="Open menu"
              >
                <MenuIcon className="h-5 w-5" />
              </button>
            </div>
          </header>

          <div className="mx-auto max-w-4xl px-4 py-12 sm:px-8">
            <motion.article
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.35 }}
            >
              {/* Hero */}
              <div className="flex items-center gap-3">
                <span className="rounded-full bg-emerald-500/10 px-3 py-1 text-xs font-medium text-emerald-300 ring-1 ring-emerald-500/25">
                  CLI Reference
                </span>
                <span className="text-xs text-gray-500">CLI reference</span>
              </div>
              <h1 className="mt-4 text-4xl font-semibold leading-tight tracking-tight text-gray-50">
                Seedmancer CLI Documentation
              </h1>
              <p className="mt-4 text-lg leading-8 text-gray-400">
                Save PostgreSQL and MySQL snapshots as CSVs in your project,
                restore them anytime, generate realistic test data with AI,
                and sync test data revisions to the cloud (Pro).
              </p>

              {/* Overview */}
              <section id="overview" className="mt-14 scroll-mt-20">
                <h2 className="text-2xl font-semibold tracking-tight text-gray-50">
                  Overview
                </h2>
                <p className="mt-3 text-base leading-7 text-gray-400">
                  Reset your database to a saved state in seconds. Each dataset is
                  a folder of CSVs plus{" "}
                  <span className="font-mono text-gray-300">schema.json</span>.
                  Name datasets what you like (
                  <span className="font-mono text-gray-300">baseline</span>,{" "}
                  <span className="font-mono text-gray-300">api-test</span>, a
                  timestamp). Push to the cloud when you want backups or to share
                  with your team.
                </p>
                <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
                  {[
                    {
                      icon: ZapIcon,
                      title: "Fast Database Reset",
                      body: "Restore your database to any saved version in seconds."
                    },
                    {
                      icon: ListIcon,
                      title: "Test Data Versioning",
                      body: "Save and manage multiple named test data versions locally and in the cloud."
                    },
                    {
                      icon: SparklesIcon,
                      title: "AI data generation",
                      body: "Run seedmancer generate to let AI produce realistic test data for every table in your schema and snapshot it as a new revision."
                    },
                    {
                      icon: CloudIcon,
                      title: "Cloud push & pull",
                      body: "Upload a dataset from your machine or download one someone else pushed."
                    },
                    {
                      icon: BotIcon,
                      title: "MCP Server",
                      body: "Built-in Model Context Protocol server so AI agents (Cursor, Claude Code) can drive Seedmancer through typed tool calls."
                    }
                  ].map(({ icon: Icon, title, body }) => (
                    <div
                      key={title}
                      className="rounded-xl border border-gray-800 bg-gray-900/40 p-5"
                    >
                      <Icon className="h-5 w-5 text-emerald-300" />
                      <p className="mt-3 font-medium text-gray-100">{title}</p>
                      <p className="mt-1 text-sm text-gray-400">{body}</p>
                    </div>
                  ))}
                </div>
              </section>

              {/* Installation */}
              <section id="installation" className="mt-14 scroll-mt-20">
                <h2 className="text-2xl font-semibold tracking-tight text-gray-50">
                  Installation
                </h2>
                <p className="mt-3 text-base leading-7 text-gray-400">
                  Download the pre-built binary for your platform from{" "}
                  <a
                    href="https://github.com/KazanKK/Seedmancer/releases"
                    className="text-emerald-400 hover:underline"
                    target="_blank"
                    rel="noreferrer"
                  >
                    GitHub Releases
                  </a>
                  .
                </p>

                <div className="mt-5 space-y-4">
                  {[
                    {
                      label: "Linux (arm64)",
                      code: `curl -L https://github.com/KazanKK/seedmancer/releases/latest/download/seedmancer_Linux_arm64 -o seedmancer
chmod +x seedmancer
sudo mv seedmancer /usr/local/bin/seedmancer`
                    },
                    {
                      label: "Linux (x86_64)",
                      code: `curl -L https://github.com/KazanKK/seedmancer/releases/latest/download/seedmancer_Linux_x86_64 -o seedmancer
chmod +x seedmancer
sudo mv seedmancer /usr/local/bin/seedmancer`
                    },
                    {
                      label: "macOS (arm64)",
                      code: `curl -L https://github.com/KazanKK/seedmancer/releases/latest/download/seedmancer_Darwin_arm64 -o seedmancer
chmod +x seedmancer
sudo mv seedmancer /usr/local/bin/seedmancer`
                    },
                    {
                      label: "Windows (x86_64) — PowerShell",
                      code: `curl.exe -L https://github.com/KazanKK/seedmancer/releases/latest/download/seedmancer_Windows_x86_64.exe -o seedmancer.exe\n# Move to a folder on your PATH, e.g.:\nMove-Item seedmancer.exe "$env:USERPROFILE\\AppData\\Local\\Microsoft\\WindowsApps\\seedmancer.exe"`
                    },
                    {
                      label: "Any platform — Go install",
                      code: `go install github.com/KazanKK/seedmancer@latest`
                    }
                  ].map(({ label, code }) => (
                    <div key={label}>
                      <p className="mb-2 flex items-center gap-2 text-sm font-medium text-gray-300">
                        <DownloadIcon className="h-4 w-4 text-emerald-400" />
                        {label}
                      </p>
                      <CopyCodeBlock
                        text={code}
                        className="rounded-xl border border-gray-800 bg-gray-950/80 p-4"
                      >
                        <pre className="overflow-x-auto pr-12 text-sm text-emerald-200">
                          <code>{code}</code>
                        </pre>
                      </CopyCodeBlock>
                    </div>
                  ))}
                </div>
              </section>

              {/* Quick Start */}
              <section id="quick-start" className="mt-14 scroll-mt-20">
                <h2 className="text-2xl font-semibold tracking-tight text-gray-50">
                  Quick Start
                </h2>
                <p className="mt-3 text-base leading-7 text-gray-400">
                  From your project root (where{" "}
                  <span className="font-mono text-gray-300">seedmancer.yaml</span>{" "}
                  will live), run:
                </p>
                <div className="mt-5 space-y-4">
                  {[
                    {
                      step: "1",
                      title: "Initialize the project",
                      command: "seedmancer init",
                      note: "Creates seedmancer.yaml and .seedmancer/. Add a database URL when prompted, or use seedmancer env add later."
                    },
                    {
                      step: "2",
                      title: "Sign in",
                      command: "seedmancer login",
                      note: "Opens the browser and saves your API token to ~/.seedmancer/credentials. Required for AI generation and cloud push/pull. Skip if you only export and seed offline."
                    },
                    {
                      step: "3",
                      title: "Export a snapshot",
                      command: `seedmancer export myapp/baseline`,
                      note: "Captures your current schema and all table data as a versioned revision (r001). The test data path can be any slash-separated name you like."
                    },
                    {
                      step: "4",
                      title: "Restore that snapshot",
                      command: `seedmancer seed myapp/baseline`,
                      note: "Clears all tables and restores from the latest revision. Add --yes for non-interactive CI runs."
                    }
                  ].map(({ step, title, command, note }) => (
                    <div
                      key={step}
                      className="rounded-xl border border-gray-800 bg-gray-900/30 p-5"
                    >
                      <div className="flex items-center gap-3">
                        <span className="flex h-6 w-6 items-center justify-center rounded-full bg-emerald-500/15 text-xs font-bold text-emerald-300 ring-1 ring-emerald-500/25">
                          {step}
                        </span>
                        <p className="font-medium text-gray-100">{title}</p>
                      </div>
                      <CopyCodeBlock
                        text={command}
                        className="mt-3 rounded-lg border border-gray-800 bg-gray-950 p-3"
                      >
                        <pre className="overflow-x-auto pr-12 text-sm text-emerald-200">
                          <code>{command}</code>
                        </pre>
                      </CopyCodeBlock>
                      <p className="mt-2 text-xs text-gray-500">{note}</p>
                    </div>
                  ))}
                </div>
              </section>

              {/* Plans */}
              <section id="plans" className="mt-14 scroll-mt-20">
                <h2 className="text-2xl font-semibold tracking-tight text-gray-50">
                  Plans and limits
                </h2>
                <p className="mt-3 text-base leading-7 text-gray-400">
                  Free includes local export, seed, and unlimited local AI
                  generation through the MCP server. Cloud AI generation (
                  <span className="font-mono text-gray-300">generate</span>,{" "}
                  <span className="font-mono text-gray-300">refresh</span>) and
                  cloud push & pull require a Pro plan. Working only on your
                  machine (
                  <span className="font-mono text-gray-300">export</span>,{" "}
                  <span className="font-mono text-gray-300">seed</span>, MCP) does
                  not require a plan upgrade. See your plan on{" "}
                  <Link
                    href="/dashboard/billing"
                    className="text-emerald-400 hover:underline"
                  >
                    Billing
                  </Link>
                  .
                </p>
                <div className="mt-6 overflow-x-auto rounded-xl border border-gray-800">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-800 bg-gray-900/60">
                        <th className="px-4 py-3 text-left font-medium text-gray-400">
                          Limit
                        </th>
                        <th className="px-4 py-3 text-left font-medium text-gray-400">
                          {SUBSCRIPTION_PLANS[SubscriptionPlanType.FREE].name}
                        </th>
                        <th className="px-4 py-3 text-left font-medium text-gray-400">
                          {SUBSCRIPTION_PLANS[SubscriptionPlanType.STARTER].name}
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-800/60 text-gray-300">
                      <tr className="bg-gray-900/30">
                        <td className="px-4 py-3">
                          AI generation & refresh (generate, refresh)
                        </td>
                        <td className="px-4 py-3 font-mono text-xs text-gray-500">
                          — (Pro only)
                        </td>
                        <td className="px-4 py-3 font-mono text-xs text-emerald-400">
                          Included
                        </td>
                      </tr>
                      <tr className="bg-gray-900/30">
                        <td className="px-4 py-3">Local AI generation via MCP</td>
                        <td className="px-4 py-3 font-mono text-xs text-emerald-400">
                          Unlimited
                        </td>
                        <td className="px-4 py-3 font-mono text-xs text-emerald-400">
                          Unlimited
                        </td>
                      </tr>
                      <tr className="bg-gray-900/30">
                        <td className="px-4 py-3">Cloud push & pull</td>
                        <td className="px-4 py-3 font-mono text-xs text-gray-500">
                          — (Pro only)
                        </td>
                        <td className="px-4 py-3 font-mono text-xs text-emerald-400">
                          Up to {SUBSCRIPTION_PLANS[SubscriptionPlanType.STARTER].limits.syncedDatasetsMax} dataset versions
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </section>

              {/* Authentication */}
              <section id="authentication" className="mt-14 scroll-mt-20">
                <h2 className="text-2xl font-semibold tracking-tight text-gray-50">
                  Authentication
                </h2>
                <p className="mt-3 text-base leading-7 text-gray-400">
                  Commands that talk to Seedmancer online (
                  <span className="font-mono text-gray-300">push</span> and{" "}
                  <span className="font-mono text-gray-300">pull</span>)
                  need an API token. Run{" "}
                  <span className="font-mono text-gray-300">seedmancer login</span>{" "}
                  to sign in from the browser (token is saved to{" "}
                  <span className="font-mono text-gray-300">
                    ~/.seedmancer/credentials
                  </span>
                  ), or create a token under{" "}
                  <Link
                    href="/dashboard/settings"
                    className="text-emerald-400 hover:underline"
                  >
                    Dashboard → API token
                  </Link>{" "}
                  and set{" "}
                  <span className="font-mono text-gray-300">
                    SEEDMANCER_API_TOKEN
                  </span>
                  . Cloud AI generation (
                  <span className="font-mono text-gray-300">generate</span>,{" "}
                  <span className="font-mono text-gray-300">refresh</span>) also
                  requires a token and a Pro plan.
                </p>
                <CodeBlock>{`seedmancer login
# or
export SEEDMANCER_API_TOKEN="sk_..."`}
                </CodeBlock>
              </section>

              {/* Configuration */}
              <section id="configuration" className="mt-14 scroll-mt-20">
                <h2 className="text-2xl font-semibold tracking-tight text-gray-50">
                  Configuration File
                </h2>
                <p className="mt-3 text-base leading-7 text-gray-400">
                  Seedmancer reads{" "}
                  <span className="font-mono text-gray-200">
                    seedmancer.yaml
                  </span>{" "}
                  from your project root. All flag values can be set here so you
                  don&apos;t need to repeat them on every command.
                </p>
                <CodeBlock>{`storage_path: .seedmancer
default_env: local
database_url: postgres://user:pass@localhost:5432/mydb

# Named databases for seedmancer seed --env local,staging
environments:
  local:
    database_url: postgres://user:pass@localhost:5432/mydb

# Tables to skip during AI generation and refresh.
# Add framework-managed tables that should never contain fake data.
exclude_tables:
  - _prisma_migrations
  - schema_migrations
  - ar_internal_metadata`}</CodeBlock>                <div className="mt-5 overflow-x-auto rounded-xl border border-gray-800">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-800 bg-gray-900/60">
                        <th className="px-4 py-3 text-left font-medium text-gray-400">
                          Key
                        </th>
                        <th className="px-4 py-3 text-left font-medium text-gray-400">
                          Description
                        </th>
                        <th className="px-4 py-3 text-left font-medium text-gray-400">
                          Default
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-800/60">
                      {[
                        {
                          key: "storage_path",
                          desc: "Directory for schema folders and datasets",
                          def: ".seedmancer"
                        },
                        {
                          key: "default_env",
                          desc: "Used when you omit --env on export/seed",
                          def: "\u2014"
                        },
                        {
                          key: "database_url",
                          desc: "Single database URL (optional if you use environments)",
                          def: "\u2014"
                        },
                        {
                          key: "environments.<name>.database_url",
                          desc: "Named database URL (see seedmancer env)",
                          def: "\u2014"
                        },
                        {
                          key: "exclude_tables",
                          desc: "List of table names to skip during AI generation and refresh (e.g. _prisma_migrations)",
                          def: "[]"
                        }
                      ].map(({ key, desc, def }) => (
                        <tr
                          key={key}
                          className="bg-gray-900/30 transition-colors hover:bg-gray-900/60"
                        >
                          <td className="px-4 py-3 font-mono text-emerald-300">
                            {key}
                          </td>
                          <td className="px-4 py-3 text-gray-300">{desc}</td>
                          <td className="px-4 py-3 font-mono text-xs text-gray-400">
                            {def}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>

              {/* On-disk layout */}
              <section id="on-disk-layout" className="mt-14 scroll-mt-20">
                <h2 className="text-2xl font-semibold tracking-tight text-gray-50">
                  On-disk layout
                </h2>
                <p className="mt-3 text-base leading-7 text-gray-400">
                  Seedmancer stores everything under{" "}
                  <span className="font-mono text-gray-200">
                    .seedmancer/test-data/
                  </span>
                  . Each test data entry is a named path (e.g.{" "}
                  <span className="font-mono text-gray-200">baseline</span> or{" "}
                  <span className="font-mono text-gray-200">qa/smoke/auth</span>
                  ) containing numbered revisions. Every revision holds CSV files
                  plus a{" "}
                  <span className="font-mono text-gray-200">dataset.sql</span>{" "}
                  for AI-generated test data.
                </p>
                <CodeBlock>{`.seedmancer/
  test-data/
    baseline/
      revisions/
        r001/
          data/
            users.csv
            orders.csv
          manifest.json
          dataset.sql
        r002/
          data/
            ...
    qa/smoke/auth/
      revisions/
        r001/
          data/
            ...`}</CodeBlock>
                <ul className="mt-4 space-y-2">
                  {[
                    "You can hand-edit the CSV files to curate test data.",
                    "The .seedmancer folder is safe to commit to git — it contains no secrets.",
                    "seedmancer pull places downloaded revisions in the same layout so seedmancer seed works immediately."
                  ].map((note) => (
                    <li
                      key={note}
                      className="flex items-start gap-2.5 text-sm text-gray-400"
                    >
                      <BookOpenIcon className="mt-0.5 h-4 w-4 shrink-0 text-emerald-500/70" />
                      <span>{note}</span>
                    </li>
                  ))}
                </ul>
              </section>

              {/* Divider */}              <div className="mt-14 flex items-center gap-4">
                <div className="h-px flex-1 bg-gray-800" />
                <span className="text-xs font-semibold uppercase tracking-widest text-gray-400">
                  Commands
                </span>
                <div className="h-px flex-1 bg-gray-800" />
              </div>

              {/* Global flags */}
              <section className="mt-10 scroll-mt-20">
                <h3 className="text-sm font-semibold uppercase tracking-widest text-gray-500">
                  Global Flags
                </h3>
                <div className="mt-3 overflow-x-auto rounded-xl border border-gray-800">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-800 bg-gray-900/60">
                        <th className="px-4 py-3 text-left font-medium text-gray-400">
                          Flag
                        </th>
                        <th className="px-4 py-3 text-left font-medium text-gray-400">
                          Description
                        </th>
                        <th className="px-4 py-3 text-left font-medium text-gray-400">
                          Env
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-800/60">
                      <tr className="bg-gray-900/30 transition-colors hover:bg-gray-900/60">
                        <td className="whitespace-nowrap px-4 py-3 font-mono text-emerald-300">
                          --debug
                        </td>
                        <td className="px-4 py-3 text-gray-300">
                          Show detailed debug output for any command
                        </td>
                        <td className="px-4 py-3 font-mono text-xs text-yellow-400/80">
                          SEEDMANCER_DEBUG
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </section>

              {/* init */}
              <CommandSection
                id="cmd-init"
                icon={TerminalSquareIcon}
                title="init"
                description="Initialize a new Seedmancer project. Creates seedmancer.yaml and the storage directory. When run interactively in a terminal, prompts for each value. Existing values in seedmancer.yaml are used as defaults."
                flags={[
                  {
                    flag: "--storage-path",
                    description:
                      "Directory to store test data files",
                    required: false,
                    default: ".seedmancer"
                  },
                  {
                    flag: "--database-url",
                    description: "Default database connection URL (PostgreSQL or MySQL)",
                    required: false,
                    default: "(prompted)"
                  }
                ]}
                example={`seedmancer init

# Non-interactive (CI)
seedmancer init \\
  --storage-path .seedmancer \\
  --database-url "postgres://user:pass@localhost:5432/mydb"`}
                notes={[
                  "Run from the root of your project. Commit seedmancer.yaml and .seedmancer/ to git so your team shares the same test data."
                ]}
              />

              {/* login */}
              <CommandSection
                id="cmd-login"
                icon={TerminalSquareIcon}
                title="login"
                description="Authenticate with Seedmancer. Opens the browser to complete sign-in, then saves the API token to ~/.seedmancer/credentials. Required for AI generation, cloud push, and pull."
                flags={[
                  {
                    flag: "--token",
                    description: "Supply an API token directly (skips the browser flow)",
                    required: false,
                    env: "SEEDMANCER_API_TOKEN"
                  },
                  {
                    flag: "--no-browser",
                    description: "Print the auth URL instead of opening it automatically",
                    required: false
                  },
                  {
                    flag: "--timeout",
                    description: "How long to wait for the browser callback",
                    required: false,
                    default: "5m"
                  }
                ]}
                example={`# Interactive — opens the browser
seedmancer login

# CI — supply token directly
seedmancer login --token sk_...
# or
export SEEDMANCER_API_TOKEN=sk_...`}
                notes={[
                  "The token is stored in ~/.seedmancer/credentials and reused automatically by every subsequent command.",
                  "You can also set SEEDMANCER_API_TOKEN in your shell or CI environment instead of running login."
                ]}
              />

              {/* status */}
              <CommandSection
                id="cmd-status"
                icon={TerminalSquareIcon}
                title="status"
                description="Show the effective configuration, authentication state, and optionally verify connectivity to the Seedmancer API. Useful for diagnosing setup problems."
                flags={[
                  {
                    flag: "--offline",
                    description: "Skip the API reachability check",
                    required: false
                  },
                  {
                    flag: "--show-db-url",
                    description: "Print the resolved database URL (masked by default)",
                    required: false
                  },
                  {
                    flag: "--json",
                    description: "Output as JSON",
                    required: false
                  }
                ]}
                example={`seedmancer status

# Show the full DB URL
seedmancer status --show-db-url`}
                notes={[
                  "Prints config file path, storage path, default env, auth source, and API token presence."
                ]}
              />

              {/* env */}
              <section id="cmd-env" className="mt-14 scroll-mt-20">
                <div className="flex items-center gap-3">
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-emerald-500/10 ring-1 ring-emerald-500/25">
                    <TerminalSquareIcon className="h-4 w-4 text-emerald-300" />
                  </div>
                  <h2 className="text-2xl font-semibold tracking-tight text-gray-50">
                    <span className="font-mono text-emerald-300">seedmancer env</span>
                  </h2>
                </div>
                <p className="mt-3 text-base leading-7 text-gray-400">
                  Manage named database environments stored in{" "}
                  <span className="font-mono text-gray-200">seedmancer.yaml</span>
                  . Once defined, pass{" "}
                  <span className="font-mono text-gray-200">--env staging</span>{" "}
                  to any command instead of repeating the full URL.
                </p>
                <div className="mt-5">
                  <p className="mb-2 text-xs font-semibold uppercase tracking-widest text-gray-500">
                    Subcommands
                  </p>
                  <div className="overflow-x-auto rounded-xl border border-gray-800">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-gray-800 bg-gray-900/60">
                          <th className="px-4 py-3 text-left font-medium text-gray-400">Subcommand</th>
                          <th className="px-4 py-3 text-left font-medium text-gray-400">Description</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-800/60">
                        {[
                          { cmd: "env list", desc: "List all configured environments" },
                          { cmd: "env current", desc: "Print the name of the default environment" },
                          { cmd: "env use <name>", desc: "Set the default environment" },
                          { cmd: "env add <name>", desc: "Add a new environment (--db-url required)" },
                          { cmd: "env remove <name>", desc: "Remove an environment" },
                          { cmd: "env show <name>", desc: "Show the URL for an environment" }
                        ].map(({ cmd, desc }) => (
                          <tr key={cmd} className="bg-gray-900/30 transition-colors hover:bg-gray-900/60">
                            <td className="whitespace-nowrap px-4 py-3 font-mono text-emerald-300">{cmd}</td>
                            <td className="px-4 py-3 text-gray-300">{desc}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
                <div className="mt-5">
                  <p className="mb-2 text-xs font-semibold uppercase tracking-widest text-gray-500">
                    Example
                  </p>
                  <CodeBlock>{`# Add a staging environment
seedmancer env add staging --db-url "postgres://user:pass@staging-db:5432/mydb"

# List all environments
seedmancer env list

# Switch the default to staging
seedmancer env use staging

# Now export / seed against staging without repeating the URL
seedmancer export myapp/baseline --env staging
seedmancer seed myapp/baseline --env staging`}</CodeBlock>
                </div>
              </section>

              {/* export */}
              <CommandSection
                id="cmd-export"
                icon={UploadIcon}
                title="export"
                description="Capture the current database state as a new immutable revision. All table data is saved as CSV files under .seedmancer/test-data/<path>/revisions/rNNN/data/. The test data path is slash-separated and you choose it (e.g. myapp/baseline, qa/smoke/auth)."
                flags={[
                  {
                    flag: "<test-data>",
                    description: "Test data path to export into (e.g. myapp/baseline)",
                    required: true
                  },
                  {
                    flag: "--env, -e",
                    description: "Named environment to read from (see seedmancer env)",
                    required: false
                  },
                  {
                    flag: "--db-url",
                    description: "Database URL — overrides --env for this run",
                    required: false,
                    env: "SEEDMANCER_DATABASE_URL"
                  },
                  {
                    flag: "--description",
                    description: "Short human-readable note for the revision",
                    required: false
                  }
                ]}
                example={`# Export using the default env from seedmancer.yaml
seedmancer export myapp/baseline

# Export with an ad-hoc URL
seedmancer export qa/smoke/auth \\
  --db-url "postgres://user:pass@localhost:5432/mydb"

# Export against a named env
seedmancer export myapp/staging --env staging`}
                notes={[
                  "Each export creates a new revision (r001, r002, …). Previous revisions are never overwritten.",
                  "CSV files are safe to commit to git. They contain no secrets.",
                  "Use seedmancer env to configure named database targets instead of repeating --db-url."
                ]}
              />

              {/* seed */}
              <CommandSection
                id="cmd-seed"
                icon={DownloadIcon}
                title="seed"
                description="Restore a test data revision into one or more databases. All tables are cleared and refilled from the saved CSV files. A schema fingerprint check guards against seeding into a structurally incompatible database."
                flags={[
                  {
                    flag: "<test-data>",
                    description: "Test data path to restore (e.g. myapp/baseline)",
                    required: true
                  },
                  {
                    flag: "--env, -e",
                    description: "Comma-separated env names to seed (e.g. local,staging)",
                    required: false
                  },
                  {
                    flag: "--db-url",
                    description: "Ad-hoc target URL — mutually exclusive with --env",
                    required: false,
                    env: "SEEDMANCER_DATABASE_URL"
                  },
                  {
                    flag: "--revision, -r",
                    description: "Seed a specific revision (e.g. r002) instead of the latest",
                    required: false
                  },
                  {
                    flag: "--force, -f",
                    description: "Skip the schema fingerprint check",
                    required: false
                  },
                  {
                    flag: "--yes, -y",
                    description: "Skip confirmation prompts (for CI)",
                    required: false
                  },
                  {
                    flag: "--continue-on-error",
                    description: "Continue seeding remaining envs after a failure",
                    required: false
                  }
                ]}
                example={`# Seed using the default env
seedmancer seed myapp/baseline

# Seed with --yes for CI
seedmancer seed myapp/baseline --yes

# Seed multiple environments at once
seedmancer seed myapp/baseline --env local,staging --yes

# Seed a specific revision
seedmancer seed myapp/baseline --revision r002`}
                notes={[
                  "Supports both PostgreSQL and MySQL.",
                  "Production-like environment names (prod, production, live) are blocked unless you pass --yes explicitly.",
                  "Run seedmancer pull first to download a cloud revision before seeding in CI."
                ]}
              />

              {/* list */}
              <CommandSection
                id="cmd-list"
                icon={ListIcon}
                title="list"
                description="Show all local test data and their latest revision. Each row shows the test data path, latest revision label, schema fingerprint, last updated time, and detected services."
                flags={[
                  {
                    flag: "--json",
                    description: "Output as JSON",
                    required: false
                  }
                ]}
                example={`seedmancer list`}
                notes={[
                  "Lists local test data only — entries stored under your .seedmancer/ folder.",
                  "Use the dashboard to browse test data you have pushed to the cloud."
                ]}
              />

              {/* history */}
              <CommandSection
                id="cmd-history"
                icon={ListIcon}
                title="history"
                description="Show all local revisions for a test data entry, newest first. Each row shows the revision label, schema fingerprint, created time, detected services, and whether AI-generated test data is present."
                flags={[
                  {
                    flag: "<test-data>",
                    description: "Test data path to inspect",
                    required: true
                  },
                  {
                    flag: "--json",
                    description: "Output as JSON",
                    required: false
                  }
                ]}
                example={`seedmancer history myapp/baseline`}
                notes={[
                  "Revisions are immutable — each export or generate appends a new one.",
                  "Use --revision rNNN on seed or check to target a specific revision."
                ]}
              />

              {/* check */}
              <CommandSection
                id="cmd-check"
                icon={TerminalSquareIcon}
                title="check"
                description="Compare a test data revision's schema fingerprint against the live database schema. Reports added, removed, and modified columns. Useful for detecting schema drift before seeding."
                flags={[
                  {
                    flag: "<test-data>",
                    description: "Test data path to check",
                    required: true
                  },
                  {
                    flag: "--env, -e",
                    description: "Named environment to compare against",
                    required: false
                  },
                  {
                    flag: "--db-url",
                    description: "Database URL to compare against",
                    required: false,
                    env: "SEEDMANCER_DATABASE_URL"
                  },
                  {
                    flag: "--revision, -r",
                    description: "Revision to check (defaults to latest)",
                    required: false
                  },
                  {
                    flag: "--json",
                    description: "Output diff as JSON",
                    required: false
                  }
                ]}
                example={`# Check the latest revision against the default env
seedmancer check myapp/baseline

# Check against a specific env
seedmancer check myapp/baseline --env staging

# Check a specific revision
seedmancer check myapp/baseline --revision r002`}
                notes={[
                  "If the fingerprints match the exit code is 0. If they differ the exit code is 1 — useful in CI gates.",
                  "Run seedmancer refresh to automatically adapt the test data to a changed schema."
                ]}
              />


              {/* generate */}
              <CommandSection
                id="cmd-generate"
                icon={SparklesIcon}
                title="generate"
                pro
                description="Generate realistic test data using AI. The CLI uses Seedmancer's AI service to produce test data for every table in your schema, then snapshots the result as a new revision."
                flags={[
                  {
                    flag: "<test-data>",
                    description: "Test data path to generate data for (e.g. baseline, qa/smoke)",
                    required: true
                  },
                  {
                    flag: "--prompt",
                    description: "Natural-language purpose of the data (e.g. 'three realistic users'). Saved on the test data and reused when omitted",
                    required: false
                  },
                  {
                    flag: "--inherit, -b",
                    description: "Seed this base test data before generating (optional)",
                    required: false
                  },
                  {
                    flag: "--env, -e",
                    description: "Named environment to generate data against (defaults to default_env)",
                    required: false
                  },
                  {
                    flag: "--db-url",
                    description: "Database URL (overrides --env for this run)",
                    required: false,
                    env: "SEEDMANCER_DATABASE_URL"
                  },
                  {
                    flag: "--token",
                    description: "Seedmancer API token",
                    required: false,
                    env: "SEEDMANCER_API_TOKEN"
                  }
                ]}
                example={`seedmancer generate baseline --prompt 'three realistic users'

seedmancer generate qa/smoke \\
  --prompt 'a few orders with line items' \\
  --db-url "postgres://user:pass@localhost:5432/mydb"`}
                notes={[
                  "Requires a Pro plan. Local generation through the MCP server stays free and unlimited.",
                  "The prompt is saved as the test data's purpose and reused by later generate and refresh runs.",
                  "Use exclude_tables in seedmancer.yaml to skip framework-managed tables like _prisma_migrations from AI generation."
                ]}
              />

              {/* refresh */}
              <CommandSection
                id="cmd-refresh"
                icon={RefreshCwIcon}
                title="refresh"
                pro
                description="Adapt existing test data to a changed database schema using AI. Detects schema drift, regenerates the test data to match the new schema in the cloud, and saves a new revision."
                flags={[
                  {
                    flag: "<test-data>",
                    description: "Test data path to refresh",
                    required: true
                  },
                  {
                    flag: "--env, -e",
                    description: "Named environment to run against",
                    required: false
                  },
                  {
                    flag: "--revision, -r",
                    description: "Base revision to refresh from (defaults to latest)",
                    required: false
                  },
                  {
                    flag: "--prompt",
                    description: "Extra context passed to the AI (e.g. 'keep user names realistic')",
                    required: false
                  },
                  {
                    flag: "--db-url",
                    description: "Database URL (overrides --env for this run)",
                    required: false,
                    env: "SEEDMANCER_DATABASE_URL"
                  },
                  {
                    flag: "--yes, -y",
                    description: "Skip the schema-diff preview and apply immediately",
                    required: false
                  },
                  {
                    flag: "--token",
                    description: "Seedmancer API token",
                    required: false,
                    env: "SEEDMANCER_API_TOKEN"
                  }
                ]}
                example={`# Preview the schema diff, then confirm
seedmancer refresh baseline

# Non-interactive (CI)
seedmancer refresh baseline --yes`}
                notes={[
                  "Requires a Pro plan.",
                  "Shows a detailed diff of added/removed columns before applying.",
                  "If the schema has not drifted, refresh exits early with no changes.",
                  "Works on test data created by generate or a previous refresh. For plain exported snapshots, run export again after migrating instead.",
                  "Use exclude_tables in seedmancer.yaml to skip framework-managed tables like _prisma_migrations from the refresh."
                ]}
              />

              {/* pull */}
              <CommandSection
                id="cmd-pull"
                icon={CloudIcon}
                title="pull"
                description="Download the latest revision of test data from the Seedmancer cloud into your local storage (Pro). Creates a new local revision in the same on-disk layout as export, so seedmancer seed works immediately after."
                flags={[
                  {
                    flag: "<test-data>",
                    description: "Test data path to download",
                    required: true
                  },
                  {
                    flag: "--token",
                    description: "Seedmancer API token",
                    required: false,
                    env: "SEEDMANCER_API_TOKEN"
                  }
                ]}
                example={`seedmancer pull myapp/baseline

# Then restore it
seedmancer seed myapp/baseline --yes`}
                notes={[
                  "Cloud push & pull requires a Pro plan.",
                  "Files are laid out identically to a local export, so you can inspect or hand-edit them."
                ]}
              />

              {/* push */}
              <CommandSection
                id="cmd-push"
                icon={UploadIcon}
                title="push"
                description="Upload the latest revision of local test data to your Seedmancer account (Pro). Each push creates a new cloud revision. Use pull on another machine to download it."
                flags={[
                  {
                    flag: "<test-data>",
                    description: "Local test data path to upload. Omit to push all local test data",
                    required: false
                  },
                  {
                    flag: "--token",
                    description: "Seedmancer API token",
                    required: false,
                    env: "SEEDMANCER_API_TOKEN"
                  }
                ]}
                example={`seedmancer push myapp/baseline

# Push all local test data at once
seedmancer push`}
                notes={[
                  "Cloud push & pull requires a Pro plan.",
                  "Open the dashboard → Tests & Data to see what is online."
                ]}
              />

              {/* MCP Server */}
              <section id="mcp" className="mt-14 scroll-mt-20">
                <div className="flex items-center gap-3">
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-emerald-500/10 ring-1 ring-emerald-500/25">
                    <BotIcon className="h-4 w-4 text-emerald-400" />
                  </div>
                  <h2 className="text-2xl font-semibold tracking-tight text-gray-50">
                    MCP Server (AI Agents)
                  </h2>
                </div>
                <p className="mt-3 text-base leading-7 text-gray-400">
                  Seedmancer includes a{" "}
                  <a
                    href="https://modelcontextprotocol.io"
                    target="_blank"
                    rel="noreferrer"
                    className="text-emerald-400 hover:underline"
                  >
                    Model Context Protocol
                  </a>{" "}
                  server so tools like Cursor or Claude Code can run
                  Seedmancer actions for you.
                </p>

                <h3 className="mt-8 text-lg font-semibold text-gray-100">
                  Running the server
                </h3>
                <CodeBlock>{`seedmancer mcp

# Optional: HTTP transport or log file — see seedmancer mcp --help
seedmancer mcp --transport http --addr :7801
seedmancer mcp --log-file /tmp/seedmancer-mcp.log`}</CodeBlock>

                <h3 className="mt-8 text-lg font-semibold text-gray-100">
                  Cursor / Claude Code config
                </h3>
                <p className="mt-3 text-base leading-7 text-gray-400">
                  Add Seedmancer to your MCP host config. Cursor reads{" "}
                  <span className="font-mono text-gray-200">
                    ~/.cursor/mcp.json
                  </span>{" "}
                  (or project-level{" "}
                  <span className="font-mono text-gray-200">
                    .cursor/mcp.json
                  </span>
                  ). Claude Code uses{" "}
                  <span className="font-mono text-gray-200">
                    claude_code_config.json
                  </span>{" "}
                  with the same{" "}
                  <span className="font-mono text-gray-200">mcpServers</span>{" "}
                  shape.
                </p>
                <CodeBlock>{`{
  "mcpServers": {
    "seedmancer": {
      "command": "seedmancer",
      "args": ["mcp", "--log-file", "/tmp/seedmancer-mcp.log"],
      "env": {
        "SEEDMANCER_API_TOKEN": "<optional; falls back to ~/.seedmancer/credentials>"
      }
    }
  }
}`}</CodeBlock>
                <p className="mt-4 text-base leading-7 text-gray-400">
                  Restart the host, then ask the agent something like:
                </p>
                <blockquote className="mt-3 rounded-lg border-l-2 border-emerald-500/40 bg-gray-900/40 px-4 py-3 text-sm italic text-gray-300">
                  Use the Seedmancer MCP to reset my database to the{" "}
                  <span className="not-italic text-emerald-300">api-test</span>{" "}
                  dataset before running Playwright.
                </blockquote>

                <p className="mt-8 text-base leading-7 text-gray-400">
                  Your client lists every tool (export, seed, push, pull, and the
                  rest). Destructive steps may ask for confirmation; seeding
                  blocks obvious production-style database names unless you
                  explicitly allow it.
                </p>
              </section>

              {/* Environment Variables */}
              <section id="env-vars" className="mt-14 scroll-mt-20">
                <h2 className="text-2xl font-semibold tracking-tight text-gray-50">
                  Environment Variables
                </h2>
                <p className="mt-3 text-base leading-7 text-gray-400">
                  <span className="font-mono text-gray-200">seedmancer login</span>{" "}
                  saves your token to{" "}
                  <span className="font-mono text-gray-200">
                    ~/.seedmancer/credentials
                  </span>
                  . You can also set{" "}
                  <span className="font-mono text-gray-200">
                    SEEDMANCER_API_TOKEN
                  </span>
                  . A <span className="font-mono text-gray-200">--token</span> flag
                  on a command wins over both.
                </p>
                <div className="mt-5 overflow-x-auto rounded-xl border border-gray-800">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-800 bg-gray-900/60">
                        <th className="px-4 py-3 text-left font-medium text-gray-400">
                          Variable
                        </th>
                        <th className="px-4 py-3 text-left font-medium text-gray-400">
                          Equivalent flag
                        </th>
                        <th className="px-4 py-3 text-left font-medium text-gray-400">
                          Used by
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-800/60">
                      {[
                        {
                          env: "SEEDMANCER_DATABASE_URL",
                          flag: "--db-url",
                          cmds: "export, seed"
                        },
                        {
                          env: "SEEDMANCER_API_TOKEN",
                          flag: "--token",
                          cmds: "push, pull, generate, refresh, login"
                        }
                      ].map(({ env, flag, cmds }) => (
                        <tr
                          key={env}
                          className="bg-gray-900/30 transition-colors hover:bg-gray-900/60"
                        >
                          <td className="px-4 py-3 font-mono text-yellow-400/90">
                            {env}
                          </td>
                          <td className="px-4 py-3 font-mono text-emerald-300">
                            {flag}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-400">
                            {cmds}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <CodeBlock>{`export SEEDMANCER_DATABASE_URL="postgres://user:pass@localhost:5432/mydb"
export SEEDMANCER_API_TOKEN="your-api-token"

seedmancer export myapp/baseline
seedmancer push   myapp/baseline
seedmancer pull   myapp/baseline
seedmancer seed   myapp/baseline --yes
seedmancer list`}</CodeBlock>
              </section>

              {/* E2E — Playwright */}
              <section id="e2e-playwright" className="mt-14 scroll-mt-20">
                <div className="flex items-center gap-3">
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-emerald-500/10 ring-1 ring-emerald-500/25">
                    <ZapIcon className="h-4 w-4 text-emerald-300" />
                  </div>
                  <h2 className="text-2xl font-semibold tracking-tight text-gray-50">
                    Playwright
                  </h2>
                </div>
                <p className="mt-3 text-base leading-7 text-gray-400">
                  The{" "}
                  <span className="font-mono text-gray-200">@seedmancer/playwright</span>{" "}
                  package extends Playwright&apos;s base test with an automatic
                  fixture that seeds the database before each test. No{" "}
                  <span className="font-mono text-gray-200">beforeEach</span>{" "}
                  hooks, no manual CLI calls.
                </p>

                <h3 className="mt-8 text-lg font-semibold text-gray-100">
                  Install
                </h3>
                <CodeBlock>{`npm install --save-dev @seedmancer/playwright @seedmancer/cli`}</CodeBlock>
                <p className="mt-2 text-sm text-gray-400">
                  <span className="font-mono text-gray-200">@seedmancer/cli</span>{" "}
                  makes the{" "}
                  <span className="font-mono text-gray-200">seedmancer</span>{" "}
                  command available locally without a global install.
                </p>

                <h3 className="mt-8 text-lg font-semibold text-gray-100">
                  Basic usage
                </h3>
                <p className="mt-2 text-sm text-gray-400">
                  Replace the{" "}
                  <span className="font-mono text-gray-200">@playwright/test</span>{" "}
                  import, declare the state with{" "}
                  <span className="font-mono text-gray-200">test.use()</span>,
                  and read named data via the{" "}
                  <span className="font-mono text-gray-200">seedmancer</span>{" "}
                  fixture. Seeding runs automatically — no hooks needed.
                </p>
                <CodeBlock>{`// tests/auth/login.spec.ts
import { test, expect } from "@seedmancer/playwright"

test.use({
  seedmancerState: "auth/login-success",
  seedmancerReset: "beforeEach",
})

test("user can login successfully", async ({ page, seedmancer }) => {
  const user = seedmancer.get("user:login")

  await page.goto("/login")
  await page.fill('[name="email"]', user.email)
  await page.fill('[name="password"]', user.password)
  await page.click('[type="submit"]')

  await expect(page.getByText("Dashboard")).toBeVisible()
})`}</CodeBlock>
                <p className="mt-2 text-sm text-gray-400">
                  <span className="font-mono text-gray-200">seedmancer.get()</span>{" "}
                  reads named values from the state&apos;s{" "}
                  <span className="font-mono text-gray-200">contract.yaml</span>.
                  Keys ending in{" "}
                  <span className="font-mono text-gray-200">Env</span>{" "}
                  resolve from the environment, so secrets stay out of the repo.
                </p>
                <CodeBlock>{`# .seedmancer/test-data/auth/login-success/contract.yaml
state: auth/login-success
purpose: A valid verified user who can log in
provides:
  user:login:
    email: login@example.com
    passwordEnv: SEEDMANCER_TEST_PASSWORD`}</CodeBlock>

                <h3 className="mt-8 text-lg font-semibold text-gray-100">
                  Different state per describe group
                </h3>
                <p className="mt-2 text-sm text-gray-400">
                  <span className="font-mono text-gray-200">test.use()</span>{" "}
                  scopes to the nearest{" "}
                  <span className="font-mono text-gray-200">describe</span>{" "}
                  block, so each group can seed a different state.
                </p>
                <CodeBlock>{`// tests/e2e/billing.spec.ts
import { test, expect } from "@seedmancer/playwright"

test.describe("Pro plan", () => {
  test.use({ seedmancerState: "billing/pro-user" })

  test("shows active subscription", async ({ page }) => {
    await page.goto("/billing")
    await expect(page.getByText("Pro — active")).toBeVisible()
  })
})

test.describe("Free plan", () => {
  test.use({ seedmancerState: "billing/free-user" })

  test("shows upgrade prompt", async ({ page }) => {
    await page.goto("/billing")
    await expect(page.getByText("Upgrade to Pro")).toBeVisible()
  })
})`}</CodeBlock>

                <h3 className="mt-8 text-lg font-semibold text-gray-100">
                  Options
                </h3>
                <p className="mt-2 text-sm text-gray-400">
                  All options are set via{" "}
                  <span className="font-mono text-gray-200">test.use()</span>{" "}
                  and can be declared at the file, describe, or config level.
                </p>
                <div className="mt-3 overflow-x-auto rounded-lg border border-gray-800">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-800 text-left">
                        <th className="px-4 py-2.5 font-medium text-gray-300">Option</th>
                        <th className="px-4 py-2.5 font-medium text-gray-300">Type</th>
                        <th className="px-4 py-2.5 font-medium text-gray-300">Description</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-800/60 text-gray-400">
                      {[
                        ["seedmancerState", "string", "State (test data) to seed. Omit to skip seeding for a test."],
                        ["seedmancerReset", `"beforeEach" | "beforeAll" | "manual"`, "When to seed. Defaults to beforeEach."],
                        ["seedmancerEnv", "string", "Target environment (--env flag). Defaults to the project default."],
                        ["seedmancerCwd", "string", "Working directory for the CLI. Defaults to process.cwd()."],
                      ].map(([opt, type, desc]) => (
                        <tr key={opt}>
                          <td className="px-4 py-2.5 font-mono text-gray-200">{opt}</td>
                          <td className="px-4 py-2.5 font-mono text-emerald-400/80">{type}</td>
                          <td className="px-4 py-2.5">{desc}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <h3 className="mt-8 text-lg font-semibold text-gray-100">
                  Pull test data before the suite (global setup)
                </h3>
                <p className="mt-2 text-sm text-gray-400">
                  Use{" "}
                  <span className="font-mono text-gray-200">@seedmancer/playwright/global-setup</span>{" "}
                  to pull the latest test data revision from Seedmancer Cloud
                  once before the suite starts. Locally the CLI falls back to
                  stored credentials from{" "}
                  <span className="font-mono text-gray-200">seedmancer login</span>.
                </p>
                <CodeBlock>{`// playwright.global-setup.ts
import { createSeedmancerGlobalSetup } from "@seedmancer/playwright/global-setup"

export default createSeedmancerGlobalSetup({
  testData: ["myapp/api-test"],
  // Token read from SEEDMANCER_API_TOKEN; omit to use stored login credentials.
  tokenEnv: "SEEDMANCER_API_TOKEN",
})`}</CodeBlock>
                <CodeBlock>{`// playwright.config.ts
import { defineConfig } from "@playwright/test"

export default defineConfig({
  globalSetup: "./playwright.global-setup.ts",
})`}</CodeBlock>

                <ul className="mt-4 space-y-2">
                  {[
                    "The default password for all auth users recreated by Seedmancer is SeedmancerTest1!",
                  ].map((note) => (
                    <li
                      key={note}
                      className="flex items-start gap-2.5 text-sm text-gray-400"
                    >
                      <BookOpenIcon className="mt-0.5 h-4 w-4 shrink-0 text-emerald-500/70" />
                      <span>{note}</span>
                    </li>
                  ))}
                </ul>
              </section>

              {/* CI / CD */}
              <section id="cicd" className="mt-14 scroll-mt-20">
                <div className="flex items-center gap-3">
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-emerald-500/10 ring-1 ring-emerald-500/25">
                    <RefreshCwIcon className="h-4 w-4 text-emerald-300" />
                  </div>
                  <h2 className="text-2xl font-semibold tracking-tight text-gray-50">
                    CI / CD
                  </h2>
                </div>
                <p className="mt-3 text-base leading-7 text-gray-400">
                  The recommended CI workflow is{" "}
                  <span className="font-mono text-gray-200">pull</span> then{" "}
                  <span className="font-mono text-gray-200">seed</span>: download
                  the dataset from the cloud and restore it into the test
                  database. Datasets can be large, so avoid committing{" "}
                  <span className="font-mono text-gray-200">.seedmancer/</span>{" "}
                  to git. Store{" "}
                  <span className="font-mono text-gray-200">
                    SEEDMANCER_API_TOKEN
                  </span>{" "}
                  as a CI secret instead.
                </p>

                <h3 className="mt-8 text-lg font-semibold text-gray-100">
                  GitHub Actions
                </h3>
                <CodeBlock>{`# .github/workflows/test.yml
name: Test

on: [push, pull_request]

jobs:
  test:
    runs-on: ubuntu-latest

    services:
      postgres:
        image: postgres:16
        env:
          POSTGRES_PASSWORD: postgres
        options: >-
          --health-cmd pg_isready
          --health-interval 10s
          --health-timeout 5s
          --health-retries 5
        ports:
          - 5432:5432

    steps:
      - uses: actions/checkout@v4

      - name: Install Seedmancer
        run: |
          curl -L https://github.com/KazanKK/seedmancer/releases/latest/download/seedmancer_Linux_x86_64 -o seedmancer
          chmod +x seedmancer && sudo mv seedmancer /usr/local/bin/seedmancer

      - name: Pull and seed database
        env:
          SEEDMANCER_API_TOKEN: \${{ secrets.SEEDMANCER_API_TOKEN }}
          SEEDMANCER_DATABASE_URL: postgres://postgres:postgres@localhost:5432/postgres
        run: |
          seedmancer pull myapp/api-test
          seedmancer seed myapp/api-test --yes

      - name: Run tests
        run: npm test`}</CodeBlock>

                <h3 className="mt-8 text-lg font-semibold text-gray-100">
                  GitLab CI
                </h3>
                <CodeBlock>{`# .gitlab-ci.yml
test:
  image: node:20
  services:
    - postgres:16
  variables:
    POSTGRES_PASSWORD: postgres
    SEEDMANCER_DATABASE_URL: postgres://postgres:postgres@postgres:5432/postgres
  before_script:
    - curl -L https://github.com/KazanKK/seedmancer/releases/latest/download/seedmancer_Linux_x86_64 -o /usr/local/bin/seedmancer
    - chmod +x /usr/local/bin/seedmancer
    - seedmancer pull myapp/api-test
    - seedmancer seed myapp/api-test --yes
  script:
    - npm test`}</CodeBlock>

                <h3 className="mt-8 text-lg font-semibold text-gray-100">
                  Docker Compose (local / CI)
                </h3>
                <CodeBlock>{`# docker-compose.test.yml
services:
  db:
    image: postgres:16
    environment:
      POSTGRES_PASSWORD: postgres
    ports:
      - "5432:5432"
    healthcheck:
      test: ["CMD", "pg_isready", "-U", "postgres"]
      interval: 5s
      retries: 5

  seed:
    image: alpine
    depends_on:
      db:
        condition: service_healthy
    volumes:
      - /usr/local/bin/seedmancer:/usr/local/bin/seedmancer
    command: sh -c "seedmancer pull myapp/api-test && seedmancer seed myapp/api-test --yes"
    environment:
      SEEDMANCER_API_TOKEN: \${SEEDMANCER_API_TOKEN}
      SEEDMANCER_DATABASE_URL: postgres://postgres:postgres@db:5432/postgres`}</CodeBlock>

                <ul className="mt-4 space-y-2">
                  {[
                    "Commit .seedmancer/ to git so CI can seed without extra setup, or keep it out of git and pull revisions from the cloud in CI (Pro).",
                    "Add SEEDMANCER_API_TOKEN to your CI secrets (GitHub → Settings → Secrets, GitLab → CI/CD Variables).",
                    "Use --yes to skip the confirmation prompt in non-interactive environments.",
                    "Push a new revision with seedmancer push myapp/api-test whenever your test data changes, then CI picks it up on the next run."
                  ].map((note) => (
                    <li
                      key={note}
                      className="flex items-start gap-2.5 text-sm text-gray-400"
                    >
                      <BookOpenIcon className="mt-0.5 h-4 w-4 shrink-0 text-emerald-500/70" />
                      <span>{note}</span>
                    </li>
                  ))}
                </ul>
              </section>

              {/* Footer spacer */}
              <div className="mt-20 border-t border-gray-800/60 pt-8">
                <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500">
                  <span>Seedmancer CLI &mdash; MIT License</span>
                  <a
                    href="https://github.com/KazanKK/Seedmancer"
                    className="flex items-center gap-1.5 text-gray-400 transition-colors hover:text-gray-200"
                    target="_blank"
                    rel="noreferrer"
                  >
                    <CodeIcon className="h-4 w-4" />
                    GitHub
                  </a>
                  <ChevronRightIcon className="h-3 w-3 text-gray-500" />
                  <span className="text-gray-400">Updated June 2026</span>
                </div>
              </div>
            </motion.article>
          </div>
        </div>
      </div>
    </main>
  )
}
